import { useState, useEffect, useCallback, ReactElement } from 'react';

type UserFaderTimer = {
  (props: { children: ReactElement[]; speed: number; itemsToShow: number; transitionDuration: number }): {
    visibleItems: ReactElement[];
    animation: boolean;
  };
};

export const useFaderTimer: UserFaderTimer = ({ children, itemsToShow, speed, transitionDuration }) => {
  const childNodes = children;

  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState<ReactElement[]>([]);
  const [animation, setAnimation] = useState(true);
  const [clock, setClock] = useState<number>();

  useEffect(() => {
    setVisibleItems(childNodes.slice(0, itemsToShow));
  }, [setVisibleItems, childNodes, itemsToShow]);

  useEffect(() => {
    setEndIndex(itemsToShow);
  }, [itemsToShow, setEndIndex]);

  const resetClock = useCallback(() => {
    if (clock) {
      clearTimeout(clock);
      setClock(undefined);
    }
  }, [clock, setClock]);

  const startClock = useCallback(() => {
    if (clock) {
      return clock;
    }

    // need to be explicit about which setTimeout we're using for typescript
    const timeout = window.setTimeout(async () => {
      let newStartIndex;
      let newEndIndex;
      if (startIndex + itemsToShow >= childNodes.length) {
        newStartIndex = 0;
        newEndIndex = itemsToShow;
      } else {
        newStartIndex = startIndex + itemsToShow;
        newEndIndex = endIndex + itemsToShow;
      }
      setStartIndex(newStartIndex);
      setEndIndex(newEndIndex);
      setAnimation(false);
      await new Promise((r) => setTimeout(r, transitionDuration));
      setVisibleItems(childNodes.slice(newStartIndex, newEndIndex));
      setAnimation(true);

      startClock();
    }, speed);

    setClock(timeout);
    return timeout;
  }, [
    clock,
    childNodes,
    endIndex,
    itemsToShow,
    speed,
    startIndex,
    setClock,
    setStartIndex,
    setEndIndex,
    setVisibleItems,
    setAnimation,
    transitionDuration,
  ]);

  useEffect(() => {
    if (speed) {
      startClock();
    }
    return () => {
      resetClock();
    };
  }, [startClock, resetClock, speed]);

  return { visibleItems, animation };
};
